import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card, Button, Input, message, Row, Col, Spin, Empty, Form, Select, Typography, Space, Tabs, Modal, Tag, DatePicker } from 'antd';
import moment from 'moment';
import {
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    ArrowLeftOutlined,
    ProjectOutlined,
    PlusOutlined,
    SettingOutlined,
    FilterOutlined,
    FileTextOutlined,
    BellOutlined
} from '@ant-design/icons';
import API from '../../../../API';
import ScopedSummarySettings from '../ScopedSummarySettings';
import EntitySummarySettingsModal from '../EntitySummarySettingsModal';
import SummarySettingsModal from '../SummarySettingsModal';
import ProjectFinanciersTab from '../ProjectFinanciersTab';
import ProjectComponentsTab from '../ProjectComponentsTab';
import ProjectSubComponentsTab from '../ProjectSubComponentsTab';
import ProjectRegionsTab from '../ProjectRegionsTab';
import SubProjectsTab from './components/SubProjectsTab';
import SendAlertModal from './components/SendAlertModal';
import CustomList from '../../../components/List';
import ListItem from '../../../components/ListItem';
import CustomPagination from '../../../components/CustomPagination';
import { useProjectRegions, usePackages, useSubProjects, useProjectCouncilStats  } from '../../../../hooks/useDataHooks';
import './ProjectDetails.css';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

const ProjectDetails = () => {
    const { projectId } = useParams();
    const history = useHistory();
    const [form] = Form.useForm();
    const [packageForm] = Form.useForm();

    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingProject, setEditingProject] = useState(false);
    const [saving, setSaving] = useState(false);
    const [projectStatuses, setProjectStatuses] = useState([]);
    const [projectStatusesLoading, setProjectStatusesLoading] = useState(false);
    const [projectComponents, setProjectComponents] = useState([]);
    const [projectComponentsLoading, setProjectComponentsLoading] = useState(false);
    const [projectSubComponents, setProjectSubComponents] = useState([]);
    const [projectSubComponentsLoading, setProjectSubComponentsLoading] = useState(false);


    const [editingPackage, setEditingPackage] = useState(null);
    const [showPackageModal, setShowPackageModal] = useState(false);

    const [packageContracts, setPackageContracts] = useState({});
    const [contractors, setContractors] = useState([]);
    const [contractorsLoading, setContractorsLoading] = useState(false);
    const [editingPackageContract, setEditingPackageContract] = useState(null);
    const [showPackageContractModal, setShowPackageContractModal] = useState(false);

    const [districts, setDistricts] = useState([]);
    const [districtsLoading, setDistrictsLoading] = useState(false);
    const [subProjectTypes, setSubProjectTypes] = useState([]);
    const [subProjectTypesLoading, setSubProjectTypesLoading] = useState(false);
    const [subProjectStatuses, setSubProjectStatuses] = useState([]);
    const [subProjectStatusesLoading, setSubProjectStatusesLoading] = useState(false);

    const [regionsPage, setRegionsPage] = useState(1);
    const [councilsPage, setCouncilsPage] = useState(1);
    const [councilStatsPage, setCouncilStatsPage] = useState(1);
    const [packagesPage, setPackagesPage] = useState(1);
    const [subProjectsPage, setSubProjectsPage] = useState(1);
    const [regionsPerPage, setRegionsPerPage] = useState(10);
    const [councilsPerPage, setCouncilsPerPage] = useState(10);
    const [councilStatsPerPage, setCouncilStatsPerPage] = useState(10);
    const [packagesPerPage, setPackagesPerPage] = useState(10);
    const [subProjectsPerPage, setSubProjectsPerPage] = useState(10);

    const defaultCouncilStatsMonth = useMemo(() => {
        return moment().subtract(1, 'month').startOf('month');
    }, []);

    const [councilStatsMonth, setCouncilStatsMonth] = useState(defaultCouncilStatsMonth);
    const [councilStatsFilterModalOpen, setCouncilStatsFilterModalOpen] = useState(false);
    const [alertCouncil, setAlertCouncil] = useState(null);
    const [councilStatsRefreshKey, setCouncilStatsRefreshKey] = useState(0);
    const [councilStatsFiltersState, setCouncilStatsFiltersState] = useState({
        submission: null,
        approval: null,
        processing: null
    });
    const [councilStatsFiltersDraft, setCouncilStatsFiltersDraft] = useState({
        submission: null,
        approval: null,
        processing: null
    });

    const councilStatsFilters = useMemo(() => {
        if (!councilStatsMonth) {
            return {};
        }
        const filters = {
            report_start_date: councilStatsMonth.clone().startOf('month').format('YYYY-MM-DD'),
            report_end_date: councilStatsMonth.clone().endOf('month').format('YYYY-MM-DD'),
        };
        if (councilStatsFiltersState.submission) {
            filters.submission_status = councilStatsFiltersState.submission;
        }
        if (councilStatsFiltersState.approval) {
            filters.approval_status = councilStatsFiltersState.approval;
        }
        if (councilStatsFiltersState.processing) {
            filters.processing_status = councilStatsFiltersState.processing;
        }
        return filters;
    }, [councilStatsMonth, councilStatsFiltersState]);

    const [regionSummaryModalVisible, setRegionSummaryModalVisible] = useState(false);
    const [regionSummaryContext, setRegionSummaryContext] = useState({ id: null, name: '' });

    const [selectedCouncilId, setSelectedCouncilId] = useState(null);
    const [councilSummaryModalVisible, setCouncilSummaryModalVisible] = useState(false);
    const [councilSummaryContext, setCouncilSummaryContext] = useState({ id: null, name: '' });

    const [showContractModal, setShowContractModal] = useState(false);
    const [editingContract, setEditingContract] = useState(null);
    const [contractForm] = Form.useForm();
    const [councilContracts, setCouncilContracts] = useState({});
    const [currencies, setCurrencies] = useState([]);
    const [currenciesLoading, setCurrenciesLoading] = useState(false);
    const [showSummarySettingsModal, setShowSummarySettingsModal] = useState(false);

    // const { councils, pagination: councilsPagination, loading: councilsLoading, error: councilsError } = useProjectCouncils(projectId, councilsPage, councilsPerPage);
    const { packages, pagination: packagesPagination, loading: packagesLoading, error: packagesError } = usePackages({ 'filter[project_id]': projectId }, packagesPage, packagesPerPage);
    const { councilStats, pagination: councilStatsPagination, loading: councilStatsLoading } = useProjectCouncilStats(projectId, councilStatsPage, councilStatsPerPage, councilStatsFilters, councilStatsRefreshKey);
    const { 
        subProjects, 
        pagination: subProjectsPagination, 
        loading: subProjectsLoading, 
        error: subProjectsError,
        fetchSubProjects,
        currentPage: subProjectsCurrentPage,
        currentPerPage: subProjectsCurrentPerPage
    } = useSubProjects({ 'filter[project_id]': projectId }, subProjectsPage, subProjectsPerPage);
 

    const [packageContractForm] = Form.useForm();

    useEffect(() => {
        if (projectId) {
            fetchProjectDetails();
            fetchProjectStatuses();
            fetchProjectComponents();
            fetchProjectSubComponents();
            fetchContractors();
            fetchDistricts();
            fetchSubProjectTypes();
            fetchSubProjectStatuses();
            fetchPackageContracts();
            fetchCouncilContracts();
            fetchCurrencies();
        } else {
            console.log('No projectId, skipping fetch functions');   
        }
    }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
    const [summaryModalVisible, setSummaryModalVisible] = useState(false);
    const [summaryModalContext, setSummaryModalContext] = useState({ entityType: null, entityName: '', entityId: null });

    const openSummaryModal = (type, name, id) => {
        setSummaryModalContext({ entityType: type, entityName: name, entityId: id });
        setSummaryModalVisible(true);
    };

    const openRegionSummaryModal = (regionId, regionName) => {
        setRegionSummaryContext({ id: regionId, name: regionName });
        setRegionSummaryModalVisible(true);
    };

    const handleRegionsPageChange = (newPage, newPerPage) => {
        setRegionsPage(newPage);
        if (newPerPage && newPerPage !== regionsPerPage) {
            setRegionsPerPage(newPerPage);
        }
    };

    const handleCouncilsPageChange = (newPage, newPerPage) => {
        setCouncilsPage(newPage);
        if (newPerPage && newPerPage !== councilsPerPage) {
            setCouncilsPerPage(newPerPage);
        }
    };

    const handleCouncilStatsPageChange = (newPage, newPerPage) => {
        setCouncilStatsPage(newPage);
        if (newPerPage && newPerPage !== councilStatsPerPage) {
            setCouncilStatsPerPage(newPerPage);
        }
    };

    const handleCouncilStatsDateChange = (value) => {
        if (value) {
            setCouncilStatsMonth(value);
        } else {
            setCouncilStatsMonth(defaultCouncilStatsMonth);
        }
        setCouncilStatsPage(1);
    };

    const handleApplyCouncilStatsFilters = () => {
        setCouncilStatsFiltersState(councilStatsFiltersDraft);
        setCouncilStatsFilterModalOpen(false);
        setCouncilStatsPage(1);
    };

    const handleResetCouncilStatsFilters = () => {
        setCouncilStatsFiltersDraft({
            submission: null,
            approval: null,
            processing: null
        });
    };

    const handlePackagesPageChange = (newPage, newPerPage) => {
        setPackagesPage(newPage);
        if (newPerPage && newPerPage !== packagesPerPage) {
            setPackagesPerPage(newPerPage);
        }
    };

    const handleSubProjectsPageChange = (newPage, newPerPage) => {
        fetchSubProjects(newPage, newPerPage || subProjectsCurrentPerPage);
    };

    const fetchProjectDetails = async () => {
        try {
            setLoading(true);
            const response = await API.getProject(projectId);
            if (response.success) {
                setProject(response.data);
            } else {
                message.error('Failed to fetch project details');
            }
        } catch (error) {
            console.error('Error fetching project:', error);
            message.error('Failed to fetch project details');
        } finally {
            setLoading(false);
        }
    };

    const fetchProjectStatuses = async () => {
        try {
            setProjectStatusesLoading(true);
            const response = await API.getProjectStatus();
            if (response.success) {
                setProjectStatuses(response.data);
            } else {
                message.warning('Failed to fetch project statuses');
            }
        } catch (error) {
            console.error('Error fetching project statuses:', error);
            message.error('Failed to fetch project statuses');
        } finally {
            setProjectStatusesLoading(false);
        }
    };

    const fetchProjectComponents = async () => {
        try {
            setProjectComponentsLoading(true);
            const response = await API.getProjectComponents();
            if (response.success) {
                setProjectComponents(response.data?.data || response.data || []);
            } else {
                message.warning('Failed to fetch project components');
            }
        } catch (error) {
            console.error('Error fetching project components:', error);
            message.error('Failed to fetch project components');
        } finally {
            setProjectComponentsLoading(false);
        }
    };

    const fetchProjectSubComponents = async () => {
        try {
            setProjectSubComponentsLoading(true);
            const response = await API.getProjectSubComponent();
            if (response.success) {
                setProjectSubComponents(response.data?.data || response.data || []);
            } else {
                message.warning('Failed to fetch project sub components');
            }
        } catch (error) {
            console.error('Error fetching project sub components:', error);
            message.error('Failed to fetch project sub components');
        } finally {
            setProjectSubComponentsLoading(false);
        }
    };


    const fetchContractors = async () => {
        try {
            setContractorsLoading(true);
            const response = await API.getContractors();
            if (response.success) {
                setContractors(response.data || []);
            } else {
                message.warning('Failed to fetch contractors');
            }
        } catch (error) {
            console.error('Error fetching contractors:', error);
            message.error('Failed to fetch contractors');
        } finally {
            setContractorsLoading(false);
        }
    };

    const fetchPackageContracts = async () => {
        try {

            const contractsResponse = await API.getProcuringEntityPackageContracts({
                'filter[project_id]': projectId
            });

            let contracts = [];
            if (contractsResponse.success) {
                contracts = contractsResponse.data || [];
            } else if (contractsResponse.data && contractsResponse.data.success) {
                contracts = contractsResponse.data.data || [];
            } else if (Array.isArray(contractsResponse)) {
                contracts = contractsResponse;
            } else if (Array.isArray(contractsResponse.data)) {
                contracts = contractsResponse.data;
            }


            const contractMap = {};

            for (const contract of contracts) {
                if (contract.procuring_entity_package_id) {
                    contractMap[contract.procuring_entity_package_id] = contract;
                }
            }

            setPackageContracts(contractMap);
        } catch (error) {
            console.error('Error fetching package contracts:', error);
            setPackageContracts({});
        }
    };

    const fetchDistricts = async () => {
        try {
            setDistrictsLoading(true);
            const regionsResponse = await API.getRegions();
            if (regionsResponse.success) {
                const regions = regionsResponse.data || [];
                const allDistricts = [];

                for (const region of regions) {
                    try {
                        const districtsResponse = await API.getDistricts(region.id);
                        if (districtsResponse.success && districtsResponse.data) {
                            allDistricts.push(...districtsResponse.data);
                        }
                    } catch (error) {
                        console.warn(`Failed to fetch districts for region ${region.id}:`, error);
                    }
                }

                setDistricts(allDistricts);
            } else {
                message.warning('Failed to fetch regions for districts');
            }
        } catch (error) {
            console.error('Error fetching districts:', error);
            message.error('Failed to fetch districts');
        } finally {
            setDistrictsLoading(false);
        }
    };

    const fetchSubProjectTypes = async () => {
        try {
            setSubProjectTypesLoading(true);
            const response = await API.getSubProjectTypes();
            if (response.success) {
                setSubProjectTypes(response.data || []);
            } else {
                message.warning('Failed to fetch sub-project types');
            }
        } catch (error) {
            console.error('Error fetching sub-project types:', error);
            message.error('Failed to fetch sub-project types');
        } finally {
            setSubProjectTypesLoading(false);
        }
    };

    const fetchSubProjectStatuses = async () => {
        try {
            setSubProjectStatusesLoading(true);
            const response = await API.getSubProjectStatus();
            if (response.success) {
                setSubProjectStatuses(response.data || []);
            } else {
                message.warning('Failed to fetch sub-project statuses');
            }
        } catch (error) {
            console.error('Error fetching sub-project statuses:', error);
            message.error('Failed to fetch sub-project statuses');
        } finally {
            setSubProjectStatusesLoading(false);
        }
    };


    const handleBack = () => {
        history.goBack();
    };

    const handleProjectEdit = () => {
        setEditingProject(true);
        if (project) {
            form.setFieldsValue({
                name: project.name || '',
                code: project.code || '',
                description: project.description || '',
                wb_project_id: project.wb_project_id || '',
                project_status_id: project.project_status?.id || ''
            });
        }
    };

    const handleProjectSave = async () => {
        try {
            setSaving(true);
            const values = await form.validateFields();
            const response = await API.updateProject(values, projectId);
            if (response.success) {
                message.success('Project updated successfully');
                setEditingProject(false);
                fetchProjectDetails();
            } else {
                message.error('Failed to update project');
            }
        } catch (error) {
            console.error('Error updating project:', error);
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                message.error(`Validation errors: ${errorMessages.join(', ')}`);
            } else {
                message.error('Failed to update project');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleProjectCancel = () => {
        setEditingProject(false);
        form.resetFields();
    };

    const handlePackageEdit = (pkg) => {
        setEditingPackage(pkg);
        packageForm.setFieldsValue({
            name: pkg?.name || '',
            description: pkg?.description || '',
            council_id: pkg?.procuring_entity?.council_id || '',
            project_component_id: pkg?.project_component_id || '',
            project_sub_component_id: pkg?.project_sub_component_id || ''
        });
        setShowPackageModal(true);
    };

    const handlePackageSave = async () => {
        try {
            setSaving(true);
            const values = await packageForm.validateFields();

            const payload = {
                ...values,
                project_id: projectId
            };

            if (editingPackage) {
                await API.updateProcuringEntityPackage(editingPackage.id, payload);
                message.success('Package updated successfully');
            } else {
                await API.createProcuringEntityPackage(payload);
                message.success('Package created successfully');
            }

            setShowPackageModal(false);
            setEditingPackage(null);
            packageForm.resetFields();
        } catch (error) {
            console.error('Error saving package:', error);
            message.error('Failed to save package');
        } finally {
            setSaving(false);
        }
    };


    const fetchCouncilContracts = async () => {
        try {

            const contractsResponse = await API.getProcuringEntityContracts({
                'filter[project_id]': projectId
            });


            let contracts = [];
            if (contractsResponse.success) {
                contracts = contractsResponse.data || [];
            } else if (contractsResponse.data && contractsResponse.data.success) {
                contracts = contractsResponse.data.data || [];
            } else if (Array.isArray(contractsResponse)) {
                contracts = contractsResponse;
            } else if (Array.isArray(contractsResponse.data)) {
                contracts = contractsResponse.data;
            }


            const contractMap = {};

            for (const contract of contracts) {
                if (contract.procuring_entity && contract.procuring_entity.council_id) {
                    contractMap[contract.procuring_entity.council_id] = contract;
                }
            }

            setCouncilContracts(contractMap);
        } catch (error) {
            console.error('Error fetching council contracts:', error);

            try {

                const procuringEntitiesResponse = await API.getProcuringEntities({
                    'filter[project_id]': projectId
                });


                let procuringEntities = [];
                if (procuringEntitiesResponse.success) {
                    procuringEntities = procuringEntitiesResponse.data || [];
                } else if (procuringEntitiesResponse.data && procuringEntitiesResponse.data.success) {
                    procuringEntities = procuringEntitiesResponse.data.data || [];
                } else if (Array.isArray(procuringEntitiesResponse)) {
                    procuringEntities = procuringEntitiesResponse;
                } else if (Array.isArray(procuringEntitiesResponse.data)) {
                    procuringEntities = procuringEntitiesResponse.data;
                }


                const contractMap = {};

                for (const procuringEntity of procuringEntities) {
                    try {
                        const contractsResponse = await API.getProcuringEntityContracts({
                            'filter[procuring_entity_id]': procuringEntity.id
                        });


                        let contracts = [];
                        if (contractsResponse.success) {
                            contracts = contractsResponse.data || [];
                        } else if (contractsResponse.data && contractsResponse.data.success) {
                            contracts = contractsResponse.data.data || [];
                        } else if (Array.isArray(contractsResponse)) {
                            contracts = contractsResponse;
                        } else if (Array.isArray(contractsResponse.data)) {
                            contracts = contractsResponse.data;
                        }

                        if (contracts.length > 0) {
                            const contract = contracts[0];
                            contractMap[procuringEntity.council_id] = contract;
                        } else {
                            console.log(`No contracts found for procuring entity ${procuringEntity.id}`);
                        }
                    } catch (contractError) {
                        console.log(`Error fetching contract for procuring entity ${procuringEntity.id}:`, contractError);
                    }
                }

                console.log('Final contract map (fallback):', contractMap);
                setCouncilContracts(contractMap);
            } catch (fallbackError) {
                console.error('Fallback approach also failed:', fallbackError);
            }
        }
    };

    const fetchCurrencies = async () => {
        try {
            setCurrenciesLoading(true);
            const response = await API.getCurrencies();
            if (response.success) {
                setCurrencies(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching currencies:', error);
        } finally {
            setCurrenciesLoading(false);
        }
    };

    const hasContract = (councilId) => {
        const hasContractResult = !!councilContracts[councilId];
        return hasContractResult;
    };

    const hasPackageContract = (packageId) => {
        const hasContractResult = !!packageContracts[packageId];
        return hasContractResult;
    };

    const handleContractEdit = (council) => {
        const existingContract = councilContracts[council.council_id];

        if (existingContract) {
            setEditingContract({ ...council, contractId: existingContract.id });

            const extractAmount = (jsonString) => {
                if (!jsonString) return '';
                try {
                    const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
                    return parsed.amount || '';
                } catch (error) {
                    console.log('Error parsing amount:', error);
                    return jsonString || '';
                }
            };

            const extractCurrency = (jsonString) => {
                if (!jsonString) return 'USD';
                try {
                    const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
                    return parsed.currency || 'USD';
                } catch (error) {
                    console.log('Error parsing currency:', error);
                    return 'USD';
                }
            };

            contractForm.setFieldsValue({
                contract_no: existingContract.contract_no || '',
                contract_name: existingContract.contract_name || '',
                original_contract_sum: extractAmount(existingContract.original_contract_sum),
                original_contract_sum_currency: extractCurrency(existingContract.original_contract_sum),
                revised_contract_sum: extractAmount(existingContract.revised_contract_sum),
                revised_contract_sum_currency: extractCurrency(existingContract.revised_contract_sum),
                contract_duration: existingContract.contract_duration || '',
                contract_duration_unit: existingContract.contract_duration_unit || '',
                commencement_date: existingContract.commencement_date || '',
                end_date_of_contract: existingContract.end_date_of_contract || '',
                addendum_signing_date: existingContract.addendum_signing_date || '',
                consortium_name: existingContract.consortium_name || '',
                consultant_contact: existingContract.consultant_contact || '',
                procuring_entity_id: council.procuring_entity_id || ''
            });
        } else {
            setEditingContract(council);
            contractForm.setFieldsValue({
                contract_no: '',
                contract_name: '',
                original_contract_sum: '',
                original_contract_sum_currency: 'USD',
                revised_contract_sum: '',
                revised_contract_sum_currency: 'USD',
                contract_duration: '',
                contract_duration_unit: '',
                commencement_date: '',
                end_date_of_contract: '',
                addendum_signing_date: '',
                consortium_name: '',
                consultant_contact: '',
                procuring_entity_id: council.procuring_entity_id || ''
            });
        }
        setShowContractModal(true);
    };

    const handleContractSave = async () => {
        try {
            setSaving(true);
            const values = await contractForm.validateFields();

            const formatMonetaryValue = (amount, currency) => {
                if (!amount) return null;
                return JSON.stringify({
                    amount: amount.toString(),
                    currency: currency || 'USD'
                });
            };

            const payload = {
                ...values,
                project_id: parseInt(projectId),
                council_id: editingContract.id,
                procuring_entity_id: editingContract.procuring_entity_id || '',
                original_contract_sum: formatMonetaryValue(values.original_contract_sum, values.original_contract_sum_currency),
                revised_contract_sum: formatMonetaryValue(values.revised_contract_sum, values.revised_contract_sum_currency)
            };

            delete payload.original_contract_sum_currency;
            delete payload.revised_contract_sum_currency;


            if (editingContract.contractId) {
                await API.updateProcuringEntityContract(editingContract.contractId, payload);
                message.success('Contract updated successfully');
            } else {
                await API.createProcuringEntityContract(payload);
                message.success('Contract created successfully');
            }

            setShowContractModal(false);
            setEditingContract(null);
            contractForm.resetFields();
            await fetchCouncilContracts();
        } catch (error) {
            console.error('Error saving contract:', error);
            message.error('Failed to save contract');
        } finally {
            setSaving(false);
        }
    };

    const handlePackageContractEdit = (packageItem) => {
        const existingContract = packageContracts[packageItem.id];

        if (existingContract) {
            setEditingPackageContract({ ...packageItem, contractId: existingContract.id });

            const extractAmount = (jsonString) => {
                if (!jsonString) return '';
                try {
                    const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
                    return parsed.amount || '';
                } catch (error) {
                    console.error('Error parsing JSON:', error);
                    return jsonString;
                }
            };

            const extractCurrency = (jsonString) => {
                if (!jsonString) return 'USD';
                try {
                    const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
                    return parsed.currency || 'USD';
                } catch (error) {
                    console.error('Error parsing JSON:', error);
                    return 'USD';
                }
            };

            packageContractForm.setFieldsValue({
                name: existingContract.name || '',
                contract_no: existingContract.contract_no || '',
                contractor_id: existingContract.contractor_id || null,
                original_contract_sum: extractAmount(existingContract.original_contract_sum),
                original_contract_sum_currency: extractCurrency(existingContract.original_contract_sum),
                revised_contract_sum: extractAmount(existingContract.revised_contract_sum),
                revised_contract_sum_currency: extractCurrency(existingContract.revised_contract_sum),
                advance_recovery_amount: extractAmount(existingContract.advance_recovery_amount),
                advance_recovery_amount_currency: extractCurrency(existingContract.advance_recovery_amount),
                deduction_of_retention_amount: extractAmount(existingContract.deduction_of_retention_amount),
                deduction_of_retention_amount_currency: extractCurrency(existingContract.deduction_of_retention_amount),
                addendum_amount_to_the_contract: extractAmount(existingContract.addendum_amount_to_the_contract),
                addendum_amount_to_the_contract_currency: extractCurrency(existingContract.addendum_amount_to_the_contract),
                date_contract_agreement_signed: existingContract.date_contract_agreement_signed ? moment(existingContract.date_contract_agreement_signed) : null,
                date_of_commencement_of_works: existingContract.date_of_commencement_of_works ? moment(existingContract.date_of_commencement_of_works) : null,
                date_possession_of_site_given: existingContract.date_possession_of_site_given ? moment(existingContract.date_possession_of_site_given) : null,
                date_of_end_of_mobilization_period: existingContract.date_of_end_of_mobilization_period ? moment(existingContract.date_of_end_of_mobilization_period) : null,
                date_of_contract_completion: existingContract.date_of_contract_completion ? moment(existingContract.date_of_contract_completion) : null,
                revised_date_of_contract_completion: existingContract.revised_date_of_contract_completion ? moment(existingContract.revised_date_of_contract_completion) : null,
                defects_liability_notification_period: existingContract.defects_liability_notification_period ? moment(existingContract.defects_liability_notification_period) : null,
                revised_contract_period: existingContract.revised_contract_period || '',
                revised_completion_date: existingContract.revised_date_of_contract_completion ? moment(existingContract.revised_date_of_contract_completion) : null,
                eot_justification: existingContract.eot_justification || ''
            });
        } else {
            setEditingPackageContract(packageItem);
            packageContractForm.resetFields();
        }

        setShowPackageContractModal(true);
    };

    const handlePackageContractSave = async () => {
        try {
            setSaving(true);
            const values = await packageContractForm.validateFields();

            const formatMonetaryValue = (amount, currency) => {
                if (!amount) return null;
                return JSON.stringify({
                    amount: amount.toString(),
                    currency: currency || 'USD'
                });
            };

            const payload = {
                ...values,
                procuring_entity_package_id: parseInt(editingPackageContract.id),
                original_contract_sum: formatMonetaryValue(values.original_contract_sum, values.original_contract_sum_currency),
                revised_contract_sum: formatMonetaryValue(values.revised_contract_sum, values.revised_contract_sum_currency),
                advance_recovery_amount: formatMonetaryValue(values.advance_recovery_amount, values.advance_recovery_amount_currency),
                deduction_of_retention_amount: formatMonetaryValue(values.deduction_of_retention_amount, values.deduction_of_retention_amount_currency),
                addendum_amount_to_the_contract: formatMonetaryValue(values.addendum_amount_to_the_contract, values.addendum_amount_to_the_contract_currency),
                revised_contract_period: values.revised_contract_period ? parseFloat(values.revised_contract_period) : null,
                date_contract_agreement_signed: values.date_contract_agreement_signed ? values.date_contract_agreement_signed.format('YYYY-MM-DD') : null,
                date_of_commencement_of_works: values.date_of_commencement_of_works ? values.date_of_commencement_of_works.format('YYYY-MM-DD') : null,
                date_possession_of_site_given: values.date_possession_of_site_given ? values.date_possession_of_site_given.format('YYYY-MM-DD') : null,
                date_of_end_of_mobilization_period: values.date_of_end_of_mobilization_period ? values.date_of_end_of_mobilization_period.format('YYYY-MM-DD') : null,
                date_of_contract_completion: values.date_of_contract_completion ? values.date_of_contract_completion.format('YYYY-MM-DD') : null,
                revised_date_of_contract_completion: values.revised_date_of_contract_completion ? values.revised_date_of_contract_completion.format('YYYY-MM-DD') : null,
                defects_liability_notification_period: values.defects_liability_notification_period ? values.defects_liability_notification_period.format('YYYY-MM-DD') : null,
                revised_completion_date: values.revised_completion_date ? values.revised_completion_date.format('YYYY-MM-DD') : null
            };

            delete payload.original_contract_sum_currency;
            delete payload.revised_contract_sum_currency;
            delete payload.advance_recovery_amount_currency;
            delete payload.deduction_of_retention_amount_currency;
            delete payload.addendum_amount_to_the_contract_currency;


            if (editingPackageContract.contractId) {
                await API.updateProcuringEntityPackageContract(editingPackageContract.contractId, payload);
                message.success('Package contract updated successfully');
            } else {
                await API.createProcuringEntityPackageContract(payload);
                message.success('Package contract created successfully');
            }

            setShowPackageContractModal(false);
            setEditingPackageContract(null);
            packageContractForm.resetFields();
            await fetchPackageContracts();
        } catch (error) {
            console.error('Error saving package contract:', error);
            message.error('Failed to save package contract');
        } finally {
            setSaving(false);
        }
    };



    const packageHeader = [
        { xxl: 8, xl: 8, lg: 8, md: 8, sm: 24, xs: 24, header: 'Name' },
        { xxl: 8, xl: 8, lg: 8, md: 8, sm: 24, xs: 24, header: 'Description' },
        { xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24, header: 'Actions' }
    ];

    const renderPackageItem = ({ item }) => (
        <ListItem
            key={item.id}
            name={item.name}
            item={item}
            isPackage={false}
        >
            <Row gutter={[8, 0]} style={{ width: '100%' }}>
                <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24} className="contentEllipse">
                    <div className="mobile-label">Name</div>
                    <Text strong>
                        {item.name}
                        {item.procuring_entity?.council?.name && (
                            <span style={{ color: '#666', fontWeight: 'normal' }}>
                                {' '}- {item.procuring_entity.council.name}
                            </span>
                        )}
                    </Text>
                </Col>
                <Col xxl={9} xl={9} lg={9} md={9} sm={24} xs={24} className="contentEllipse">
                    <div className="mobile-label">Description</div>
                    <Text ellipsis>{item.description || '—'}</Text>
                </Col>
                <Col xxl={7} xl={7} lg={7} md={7} sm={24} xs={24} className="contentEllipse" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div className="mobile-label">Actions</div>
                    <div style={{ 
                        width: '100%', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '4px'
                    }}>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handlePackageEdit(item)}
                            size="large"
                            style={{ padding: '10px' }}
                            title="Edit"
                        />
                        <Button
                            type="text"
                            icon={<SettingOutlined />}
                            onClick={() => openSummaryModal('package', item?.name, item?.id)}
                            size="large"
                            title="Summary Settings"
                        />
                        <Button
                            type="text"
                            icon={hasPackageContract(item.id) ? <EditOutlined /> : <PlusOutlined />}
                            onClick={() => handlePackageContractEdit(item)}
                            size="large"
                            style={{ padding: '10px' }}
                            title={hasPackageContract(item.id) ? "Edit Contract" : "Create Contract"}
                            aria-label={hasPackageContract(item.id) ? "Edit Contract" : "Create Contract"}
                        />
                    </div>
                </Col>
            </Row>
        </ListItem>
    );

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'N/A';
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };


    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!project) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Empty description="Project not found" />
            </div>
        );
    }

    return (
        <div style={{ padding: '8px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                    style={{ marginBottom: '16px' }}
                >
                    Back to Projects
                </Button>
                <Title level={1} style={{ 
                    margin: 0, 
                    fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    hyphens: 'auto'
                }}>
                    <ProjectOutlined style={{ marginRight: '8px' }} />
                    {project?.name}
                </Title>
                <Text type="secondary">{project?.code || "==//=="}</Text>
            </div>

            <Tabs defaultActiveKey="basic" size="large" className="prst-tabs">
                <TabPane tab="Basic Information" key="basic">
                    <Tabs defaultActiveKey="details" size="middle">
                        <TabPane tab="Details" key="details">
                            <Card>
                                <Row gutter={[24, 24]}>
                                    <Col xs={24} lg={12}>
                                        {editingProject ? (
                                            <Form form={form} layout="vertical" onFinish={handleProjectSave}>
                                                <Form.Item
                                                    name="name"
                                                    label="Project Name"
                                                    rules={[{ required: true, message: 'Project name is required' }]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    name="code"
                                                    label="Project Code"
                                                    rules={[{ required: true, message: 'Project code is required' }]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    name="wb_project_id"
                                                    label="World Bank Project ID"
                                                    rules={[{ required: true, message: 'World Bank Project ID is required' }]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    name="project_status_id"
                                                    label="Project Status"
                                                    rules={[{ required: true, message: 'Project Status is required' }]}
                                                >
                                                    <Select
                                                        placeholder="Select project status"
                                                        loading={projectStatusesLoading}
                                                    >
                                                        {projectStatuses?.map(({ id, name }) => (
                                                            <Select.Option key={id} value={id}>
                                                                {name ? name.charAt(0).toUpperCase() + name.slice(1) : 'Choose Status'}
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item name="description" label="Description">
                                                    <TextArea rows={3} />
                                                </Form.Item>
                                                <Space>
                                                    <Button
                                                        type="primary"
                                                        icon={<SaveOutlined />}
                                                        htmlType="submit"
                                                        loading={saving}
                                                    >
                                                        Save Changes
                                                    </Button>
                                                    <Button
                                                        icon={<CloseOutlined />}
                                                        onClick={handleProjectCancel}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Space>
                                            </Form>
                                        ) : (
                                            <div style={{ padding: '16px 0' }}>
                                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                                    <div>
                                                        <Text strong>World Bank Project ID:</Text>
                                                        <br />
                                                        <Text>{project?.wb_project_id || 'N/A'}</Text>
                                                    </div>
                                                    <div>
                                                        <Text strong>Status:</Text>
                                                        <br />
                                                        <Text>{project?.project_status?.name || 'N/A'}</Text>
                                                    </div>
                                                    <div>
                                                        <Text strong>Description:</Text>
                                                        <br />
                                                        <Text>{project?.description || 'No description available'}</Text>
                                                    </div>
                                                <Space>
                                                    <Button
                                                        type="primary"
                                                        icon={<EditOutlined />}
                                                        onClick={handleProjectEdit}
                                                    >
                                                        Edit Project
                                                    </Button>
                                                    <Button
                                                        type="default"
                                                        icon={<SettingOutlined />}
                                                        onClick={() => setShowSummarySettingsModal(true)}
                                                    >
                                                        Summary Settings
                                                    </Button>
                                                </Space>
                                                </Space>
                                            </div>
                                        )}
                                    </Col>
                                    <Col xs={24} lg={12}>
                                        <div style={{ padding: '16px 0' }}>
                                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                                <div>
                                                    <Text strong>Approval Date:</Text>
                                                    <br />
                                                    <Text>{formatDate(project?.approval_date)}</Text>
                                                </div>
                                                <div>
                                                    <Text strong>Closing Date:</Text>
                                                    <br />
                                                    <Text>{formatDate(project?.closing_date)}</Text>
                                                </div>
                                                <div>
                                                    <Text strong>Total Cost:</Text>
                                                    <br />
                                                    <Text>{formatCurrency(project?.total_project_cost?.amount, project?.total_project_cost?.currency?.iso)}</Text>
                                                </div>
                                                <div>
                                                    <Text strong>Commitment Amount:</Text>
                                                    <br />
                                                    <Text>{formatCurrency(project?.commitment_amount?.amount, project?.commitment_amount?.currency?.iso)}</Text>
                                                </div>
                                                <div>
                                                    <Text strong>Regions:</Text>
                                                    <br />
                                                    <Text>{project?.regions?.map(r => r.name).join(', ') || 'N/A'}</Text>
                                                </div>
                                            </Space>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        </TabPane>

                        <TabPane tab="Project Regions" key="regions">
                            <ProjectRegionsTab 
                                projectId={projectId} 
                                onOpenSummaryModal={openRegionSummaryModal}
                            />
                        </TabPane>

                        <TabPane tab="ProjectFinanciers" key="financiers">
                            <ProjectFinanciersTab projectId={projectId} />
                        </TabPane>

                        <TabPane tab="Components" key="components">
                            <ProjectComponentsTab projectId={projectId} />
                        </TabPane>

                        <TabPane tab="Sub Components" key="sub-components">
                            <ProjectSubComponentsTab projectId={projectId} />
                        </TabPane>
                    </Tabs>
                </TabPane>

                <TabPane tab="Councils" key="councils">
                    <Card>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={24}>
                                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <Title level={4} style={{ margin: 0 }}>Councils</Title>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <DatePicker
                                            picker="month"
                                            value={councilStatsMonth}
                                            onChange={handleCouncilStatsDateChange}
                                            placeholder="Select month"
                                            size="middle"
                                            format="YYYY-MM"
                                            allowClear
                                        />
                                        <Button
                                            icon={<FilterOutlined />}
                                            onClick={() => {
                                                setCouncilStatsFiltersDraft(councilStatsFiltersState);
                                                setCouncilStatsFilterModalOpen(true);
                                            }}
                                        >
                                            Filter
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    {councilStatsLoading ? (
                                        <Spin style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}/>
                                    ) : (
                                        <>
                                            <CustomList
                                                itemName="Councils"
                                                items={councilStats}
                                                page={councilStatsPage}
                                                itemCount={councilStatsPagination?.total || (councilStats?.length || 0)}
                                                loading={councilStatsLoading}
                                                headerLayout={[
                                                    { xxl: 5, xl: 5, lg: 5, md: 5, sm: 12, xs: 24, header: 'Name' },
                                                    { xxl: 3, xl: 3, lg: 3, md: 3, sm: 12, xs: 24, header: 'Submission' },
                                                    { xxl: 2, xl: 2, lg: 2, md: 2, sm: 12, xs: 24, header: 'Duplicated' },
                                                    { xxl: 3, xl: 3, lg: 3, md: 3, sm: 12, xs: 24, header: 'Approval' },
                                                    { xxl: 3, xl: 3, lg: 3, md: 3, sm: 12, xs: 24, header: 'Processing' },
                                                    { xxl: 4, xl: 4, lg: 4, md: 4, sm: 12, xs: 24, header: 'Last reminded' },
                                                    { xxl: 4, xl: 4, lg: 4, md: 4, sm: 12, xs: 24, header: 'Actions' }
                                                ]}
                                                renderListItem={({ item }) => (
                                                    <ListItem
                                                        key={item.council_id}
                                                        name={item.council_name}
                                                        item={item}
                                                        isPackage={false}
                                                        className={selectedCouncilId === item.council_id ? 'selected' : ''}
                                                        onClick={() => setSelectedCouncilId(item.council_id)}
                                                        renderActions={() => null}
                                                    >
                                                        <Row gutter={[8, 0]} style={{ width: '100%' }}>
                                                            <Col xxl={5} xl={5} lg={5} md={5} sm={12} xs={24} title={item.council_name} className="contentEllipse">
                                                                <div className="mobile-label">Name</div>
                                                                <Text strong>{item.council_name}</Text>
                                                            </Col>
                                                            <Col xxl={3} xl={3} lg={3} md={3} sm={12} xs={24} className="contentEllipse">
                                                                <div className="mobile-label">Submission</div>
                                                                <Tag
                                                                    color={(item.status || '').toLowerCase() === 'submitted' ? 'green' : 'red'}
                                                                    style={{ fontSize: 14 }}
                                                                >
                                                                    {(item.status || '').toLowerCase() === 'submitted' ? 'Submitted' : 'Not Submitted'}
                                                                </Tag>
                                                            </Col>
                                                            <Col xxl={2} xl={2} lg={2} md={2} sm={12} xs={24} className="contentEllipse">
                                                                <div className="mobile-label">Duplicated</div>
                                                                <Text style={{ display: 'block', textAlign: "center"}}>{item.duplicate_count}</Text>
                                        
                                                            </Col>
                                                            <Col xxl={3} xl={3} lg={3} md={3} sm={12} xs={24} className="contentEllipse">
                                                                <div className="mobile-label">Approval</div>
                                                                <Tag
                                                                    color={(item.approval_status || '').toLowerCase() === 'approved' ? 'green' : 'red'}
                                                                    style={{ fontSize: 14 }}
                                                                >
                                                                    {(item.approval_status || '').toLowerCase() === 'approved' ? 'Approved' : 'Not Approved'}
                                                                </Tag>
                                                            </Col>
                                                            <Col xxl={3} xl={3} lg={3} md={3} sm={12} xs={24} className="contentEllipse">
                                                                <div className="mobile-label">Processing</div>
                                                                <Tag
                                                                    color={item.processing_status === null ? 'red' : item.processing_status ? 'green' : 'orange'}
                                                                    style={{ fontSize: 14 }}
                                                                >
                                                                    {item.processing_status === null ? 'Not Started' : item.processing_status === false ? 'Pending' : 'Completed'}
                                                                </Tag>
                                                            </Col>
                                                            <Col xxl={4} xl={4} lg={4} md={4} sm={12} xs={24} className="contentEllipse">
                                                                <div className="mobile-label">Last reminded</div>
                                                                <Text>{item.last_alert_sent_at ? moment(item.last_alert_sent_at).format('YYYY-MM-DD HH:mm') : '-'}</Text>
                                                            </Col>
                                                            <Col xxl={4} xl={4} lg={4} md={4} sm={12} xs={24} className="contentEllipse" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                                <div className="mobile-label">Actions</div>
                                                                <div style={{ 
                                                                    width: '100%', 
                                                                    display: 'flex', 
                                                                    justifyContent: 'left', 
                                                                    alignItems: 'center',
                                                                    flexWrap: 'wrap',
                                                                    gap: '4px'
                                                                }}>
                                                                    <Button
                                                                        type="text"
                                                                        icon={<BellOutlined />}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setAlertCouncil(item);
                                                                        }}
                                                                        size="large"
                                                                        style={{ padding: '10px' }}
                                                                        title="Send Alert"
                                                                        aria-label="Send Alert"
                                                                    />
                                                                    <Button
                                                                        type="text"
                                                                        icon={<SettingOutlined />}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedCouncilId(item.council_id);
                                                                            setCouncilSummaryContext({ id: item.council_id, name: item.council_name });
                                                                            setCouncilSummaryModalVisible(true);
                                                                        }}
                                                                        size="large"
                                                                        style={{ padding: '10px' }}
                                                                        title="Summary Settings"
                                                                    />
                                                                    <Button
                                                                        type="text"
                                                                        icon={hasContract(item.council_id) ? <EditOutlined /> : <PlusOutlined />}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleContractEdit(item);
                                                                        }}
                                                                        size="large"
                                                                        style={{ padding: '10px' }}
                                                                        title={hasContract(item.council_id) ? "Edit Contract" : "Create Contract"}
                                                                        aria-label={hasContract(item.council_id) ? "Edit Contract" : "Create Contract"}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </ListItem>
                                                )}
                                                showToolbar={false}
                                            />
                                            
                                            {councilStatsPagination && councilStatsPagination.total > 0 && (
                                                <CustomPagination 
                                                    pagination={councilStatsPagination}
                                                    onPageChange={handleCouncilStatsPageChange}
                                                    loading={councilStatsLoading}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </TabPane>

                <TabPane tab="Packages" key="packages">
                    <Card>
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Title level={4} style={{ margin: 0 }}>Packages</Title>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setEditingPackage(null);
                                    packageForm.resetFields();
                                    setShowPackageModal(true);
                                }}
                            >
                                Add Package
                            </Button>
                        </div>
                        <CustomList
                            itemName="Packages"
                            items={packages}
                            page={packagesPage}
                            itemCount={packagesPagination?.total || (packages?.length || 0)}
                            loading={packagesLoading}
                            headerLayout={packageHeader}
                            renderListItem={renderPackageItem}
                            showToolbar={false}
                        />
                        
                        {packagesPagination && packagesPagination.total > 0 && (
                            <CustomPagination 
                                pagination={packagesPagination}
                                onPageChange={handlePackagesPageChange}
                                loading={packagesLoading}
                            />
                        )}
                    </Card>
                </TabPane>

                <TabPane tab="Sub-Projects" key="sub-projects">
                    <SubProjectsTab
                        projectId={projectId}
                        subProjects={subProjects}
                        subProjectsPagination={subProjectsPagination}
                        subProjectsLoading={subProjectsLoading}
                        subProjectsPage={subProjectsCurrentPage}
                        subProjectsPerPage={subProjectsCurrentPerPage}
                        onPageChange={handleSubProjectsPageChange}
                        councils={councilStats}
                        packages={packages}
                        districts={districts}
                        subProjectTypes={subProjectTypes}
                        subProjectStatuses={subProjectStatuses}
                        districtsLoading={districtsLoading}
                        subProjectTypesLoading={subProjectTypesLoading}
                        subProjectStatusesLoading={subProjectStatusesLoading}
                        openSummaryModal={openSummaryModal}
                    />
                </TabPane>



            </Tabs>


            <EntitySummarySettingsModal
                visible={summaryModalVisible}
                onClose={() => setSummaryModalVisible(false)}
                entityType={summaryModalContext.entityType}
                entityName={summaryModalContext.entityName}
                entityId={summaryModalContext.entityId}
            />

            <EntitySummarySettingsModal
                visible={regionSummaryModalVisible}
                onClose={() => setRegionSummaryModalVisible(false)}
                entityType="project_region"
                entityName={regionSummaryContext.name}
                entityId={regionSummaryContext.id}
                customEndpoint={regionSummaryContext.id ? `/projects/${projectId}/regions/${regionSummaryContext.id}/summary-data` : null}
            />

            <EntitySummarySettingsModal
                visible={councilSummaryModalVisible}
                onClose={() => setCouncilSummaryModalVisible(false)}
                entityType="project_council"
                entityName={councilSummaryContext.name}
                entityId={{ projectId, councilId: councilSummaryContext.id }}
                customEndpoint={councilSummaryContext.id ? `/projects/${projectId}/councils/${councilSummaryContext.id}/summary-data` : null}
            />

            <SummarySettingsModal
                visible={showSummarySettingsModal}
                onCancel={() => setShowSummarySettingsModal(false)}
                projectId={projectId}
                projectName={project?.name}
            />

            <SendAlertModal
                visible={!!alertCouncil}
                onClose={() => setAlertCouncil(null)}
                projectId={projectId}
                council={alertCouncil}
                period={{
                    report_start_date: councilStatsFilters.report_start_date,
                    report_end_date: councilStatsFilters.report_end_date
                }}
                onSent={() => setCouncilStatsRefreshKey((key) => key + 1)}
            />

            <Modal
                title="Filter Councils"
                open={councilStatsFilterModalOpen}
                onCancel={() => setCouncilStatsFilterModalOpen(false)}
                onOk={handleApplyCouncilStatsFilters}
                okText="Apply"
                cancelText="Cancel"
            >
                <Form layout="vertical">
                    <Form.Item label="Submission Status">
                        <Select
                            placeholder="All"
                            allowClear
                            value={councilStatsFiltersDraft.submission}
                            onChange={(value) =>
                                setCouncilStatsFiltersDraft((prev) => ({ ...prev, submission: value }))
                            }
                        >
                            <Select.Option value="submitted">Submitted</Select.Option>
                            <Select.Option value="not_submitted">Not Submitted</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="Approval Status">
                        <Select
                            placeholder="All"
                            allowClear
                            value={councilStatsFiltersDraft.approval}
                            onChange={(value) =>
                                setCouncilStatsFiltersDraft((prev) => ({ ...prev, approval: value }))
                            }
                        >
                            <Select.Option value="approved">Approved</Select.Option>
                            <Select.Option value="not_approved">Not Approved</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="Processing Status">
                        <Select
                            placeholder="All"
                            allowClear
                            value={councilStatsFiltersDraft.processing}
                            onChange={(value) =>
                                setCouncilStatsFiltersDraft((prev) => ({ ...prev, processing: value }))
                            }
                        >
                            <Select.Option value="not_started">Not Started</Select.Option>
                            <Select.Option value="pending">Pending</Select.Option>
                            <Select.Option value="completed">Completed</Select.Option>
                        </Select>
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleResetCouncilStatsFilters}>Reset</Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                title={editingPackage ? 'Edit Package' : 'Add Package'}
                open={showPackageModal}
                onOk={handlePackageSave}
                onCancel={() => {
                    setShowPackageModal(false);
                    setEditingPackage(null);
                    packageForm.resetFields();
                }}
                confirmLoading={saving}
                width={600}
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                <Form form={packageForm} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Package Name"
                        rules={[{ required: true, message: 'Package name is required' }]}
                    >
                        <Input placeholder="Enter package name" />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea rows={3} placeholder="Enter package description" />
                    </Form.Item>
                    <Form.Item
                        name="council_id"
                        label="Council"
                        rules={[{ required: true, message: 'Council is required' }]}
                    >
                        <Select
                            placeholder="Select council"
                            loading={councilStatsLoading}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {Array.isArray(councilStats) ? councilStats.map((council) => {
                                return (
                                    <Select.Option key={council.council_id} value={council.council_id}>
                                        {council.council_name}
                                    </Select.Option>
                                );
                            }) : []}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="project_component_id"
                        label="Project Component"
                    >
                        <Select
                            placeholder="Select project component"
                            loading={projectComponentsLoading}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {projectComponents?.map((component) => (
                                <Select.Option key={component.id} value={component.id}>
                                    {component.name}
                                </Select.Option>
                            )) || []}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="project_sub_component_id"
                        label="Project Sub Component"
                    >
                        <Select
                            placeholder="Select project sub component"
                            loading={projectSubComponentsLoading}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {projectSubComponents?.map((subComponent) => (
                                <Select.Option key={subComponent.id} value={subComponent.id}>
                                    {subComponent.name}
                                </Select.Option>
                            )) || []}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={editingContract?.contractId ? 'Edit CSC Contract' : 'Create CSC Contract'}
                open={showContractModal}
                onOk={handleContractSave}
                onCancel={() => {
                    setShowContractModal(false);
                    setEditingContract(null);
                    contractForm.resetFields();
                }}
                confirmLoading={saving}
                width={800}
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                <Form form={contractForm} layout="vertical">
                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="contract_no"
                                label="Contract Number"
                                rules={[{ required: true, message: 'Contract number is required' }]}
                            >
                                <Input placeholder="Enter contract number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="contract_name"
                                label="Contract Name"
                            >
                                <Input placeholder="Enter contract name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="original_contract_sum"
                                label="Original Contract Sum"
                            >
                                <Input type="number" placeholder="Enter original contract sum" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="original_contract_sum_currency"
                                label="Original Contract Sum Currency"
                            >
                                <Select placeholder="Select currency" defaultValue="USD" loading={currenciesLoading}>
                                    {currencies.map(currency => (
                                        <Select.Option key={currency.id} value={currency.iso}>
                                            {currency.iso} - {currency.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="revised_contract_sum"
                                label="Revised Contract Sum"
                            >
                                <Input type="number" placeholder="Enter revised contract sum" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="revised_contract_sum_currency"
                                label="Revised Contract Sum Currency"
                            >
                                <Select placeholder="Select currency" defaultValue="USD" loading={currenciesLoading}>
                                    {currencies.map(currency => (
                                        <Select.Option key={currency.id} value={currency.iso}>
                                            {currency.iso} - {currency.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="contract_duration"
                                label="Contract Duration"
                            >
                                <Input placeholder="Enter contract duration" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="contract_duration_unit"
                                label="Contract Duration Unit"
                            >
                                <Select placeholder="Select duration unit">
                                    <Select.Option value="days">Days</Select.Option>
                                    <Select.Option value="weeks">Weeks</Select.Option>
                                    <Select.Option value="months">Months</Select.Option>
                                    <Select.Option value="years">Years</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="commencement_date"
                                label="Commencement Date"
                            >
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="end_date_of_contract"
                                label="End Date of Contract"
                            >
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="addendum_signing_date"
                                label="Addendum Signing Date"
                            >
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="consortium_name"
                                label="Consortium Name"
                            >
                                <Input placeholder="Enter consortium name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="consultant_contact"
                        label="Consultant Contact"
                    >
                        <TextArea rows={3} placeholder="Enter consultant contact information" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={editingPackageContract?.contractId ? 'Edit Package Contract' : 'Create Package Contract'}
                open={showPackageContractModal}
                onOk={handlePackageContractSave}
                onCancel={() => {
                    setShowPackageContractModal(false);
                    setEditingPackageContract(null);
                    packageContractForm.resetFields();
                }}
                confirmLoading={saving}
                width={800}
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                <Form form={packageContractForm} layout="vertical">
                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="name"
                                label="Contract Name"
                                rules={[{ required: true, message: 'Contract name is required' }]}
                            >
                                <Input placeholder="Enter contract name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="contract_no"
                                label="Contract Number"
                                rules={[{ required: true, message: 'Contract number is required' }]}
                            >
                                <Input placeholder="Enter contract number" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                            <Form.Item
                                name="contractor_id"
                                label="Contractor"
                                rules={[{ required: true, message: 'Contractor is required' }]}
                            >
                                <Select
                                    placeholder="Select contractor"
                                    loading={contractorsLoading}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {contractors?.map(contractor => (
                                        <Select.Option key={contractor.id} value={contractor.id}>
                                            {contractor.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="original_contract_sum"
                                label="Original Contract Sum"
                            >
                                <Input type="number" placeholder="Enter original contract sum" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="original_contract_sum_currency"
                                label="Original Contract Sum Currency"
                            >
                                <Select placeholder="Select currency" defaultValue="USD" loading={currenciesLoading}>
                                    {currencies.map(currency => (
                                        <Select.Option key={currency.id} value={currency.iso}>
                                            {currency.iso} - {currency.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="revised_contract_sum"
                                label="Revised Contract Sum"
                            >
                                <Input type="number" placeholder="Enter revised contract sum" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="revised_contract_sum_currency"
                                label="Revised Contract Sum Currency"
                            >
                                <Select placeholder="Select currency" defaultValue="USD" loading={currenciesLoading}>
                                    {currencies.map(currency => (
                                        <Select.Option key={currency.id} value={currency.iso}>
                                            {currency.iso} - {currency.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="advance_recovery_amount"
                                label="Advance Recovery Amount"
                            >
                                <Input type="number" placeholder="Enter advance recovery amount" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="advance_recovery_amount_currency"
                                label="Advance Recovery Amount Currency"
                            >
                                <Select placeholder="Select currency" defaultValue="USD" loading={currenciesLoading}>
                                    {currencies.map(currency => (
                                        <Select.Option key={currency.id} value={currency.iso}>
                                            {currency.iso} - {currency.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="deduction_of_retention_amount"
                                label="Deduction of Retention Amount"
                            >
                                <Input type="number" placeholder="Enter deduction of retention amount" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="deduction_of_retention_amount_currency"
                                label="Deduction of Retention Amount Currency"
                            >
                                <Select placeholder="Select currency" defaultValue="USD" loading={currenciesLoading}>
                                    {currencies.map(currency => (
                                        <Select.Option key={currency.id} value={currency.iso}>
                                            {currency.iso} - {currency.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="addendum_amount_to_the_contract"
                                label="Addendum Amount to the Contract"
                            >
                                <Input type="number" placeholder="Enter addendum amount" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="addendum_amount_to_the_contract_currency"
                                label="Addendum Amount Currency"
                            >
                                <Select placeholder="Select currency" defaultValue="USD" loading={currenciesLoading}>
                                    {currencies.map(currency => (
                                        <Select.Option key={currency.id} value={currency.iso}>
                                            {currency.iso} - {currency.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="date_contract_agreement_signed"
                                label="Date Contract Agreement Signed"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="date_of_commencement_of_works"
                                label="Date of Commencement of Works"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="date_possession_of_site_given"
                                label="Date Possession of Site Given"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="date_of_end_of_mobilization_period"
                                label="Date of End of Mobilization Period"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="date_of_contract_completion"
                                label="Date of Contract Completion"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="revised_date_of_contract_completion"
                                label="Revised Date of Contract Completion"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="defects_liability_notification_period"
                                label="Defects Liability Notification Period"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="revised_contract_period"
                                label="Revised Contract Period"
                            >
                                <Input type="number" placeholder="Enter revised contract period" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, { xs: 16, sm: 16, md: 16, lg: 16, xl: 16, xxl: 16 }]}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <Form.Item
                                name="revised_completion_date"
                                label="Revised Completion Date"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="eot_justification"
                        label="EOT Justification"
                    >
                        <TextArea rows={3} placeholder="Enter EOT justification" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ProjectDetails; 
